import {
  Component,
  ChangeDetectionStrategy,
  Inject,
  ViewChild,
} from '@angular/core';
import { filter, Observable, of, tap } from 'rxjs';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import cloneDeep from 'lodash.clonedeep';

import {
  DprForecastActivityCustomDataGridData,
  DprForecastActivityGridData,
  DprForecastSubActivityGridData,
} from '@dpr-details/models/grid';
import {
  DialogComponent,
  DialogContext,
  DialogData,
  GridApi,
  GridSync,
} from '@grid/models';
import { BaseDialogComponent } from '@shared/models/component';
import {
  ActivityMobilisation,
  CustomData,
  CustomDataValue,
} from '@shared/models/project-setup';
import { AppFacade, UserDetailsFacade } from '@shared/state/facades';
import { SelectedSubproject } from '@shared/models';
import { StaticMessage } from '@shared/enums';
import { DprForecastData } from '@dpr-details/enums';
import { DprActivityCustomDataService } from '@dpr-details/services';
import { DprEditActivitySubActivitiesGridComponent } from '../dpr-edit-activity-subactivities-grid/dpr-edit-activity-subactivities-grid.component';
import { DprEditActivityCustomDataGridComponent } from '../dpr-edit-activity-custom-data-grid/dpr-edit-activity-custom-data-grid.component';
import { canDeactivateUnsavedChanges } from '@shared/guards';

@UntilDestroy()
@Component({
  selector: 'app-dpr-edit-forecast-activity-dialog-edit',
  templateUrl: './dpr-edit-forecast-activity-dialog-edit.component.html',
  styleUrls: ['./dpr-edit-forecast-activity-dialog-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprEditForecastActivityDialogEditComponent
  extends BaseDialogComponent
  implements DialogComponent<DprForecastActivityGridData>
{
  //#region Templates

  @ViewChild('subActivitiesGrid', { static: true })
  subActivitiesGrid: DprEditActivitySubActivitiesGridComponent;

  @ViewChild('customDataGrid', { static: true })
  customDataGrid: DprEditActivityCustomDataGridComponent;

  //#endregion

  title: string;
  context: DialogContext<DprForecastActivityGridData>;
  selectedSubproject: SelectedSubproject;
  DprForecastData = DprForecastData;
  activeLink: DprForecastData;

  userDetailsQuery = this.userDetailsFacade.query;
  isAppOnline$ = this.appFacade.query.isAppOnline$;

  subActivitiesGridApi: GridApi<DprForecastSubActivityGridData>;
  customDataGridApi: GridApi<DprForecastActivityCustomDataGridData>;

  dprActivityCustomDataGridData: DprForecastActivityCustomDataGridData[] = [];
  activityMobilisation: ActivityMobilisation[];
  customDataList: CustomData[] = [];
  customDataListValues: CustomDataValue[] = [];

  StaticMessage = StaticMessage;

  isMilestoneActivity: boolean;
  constructor(
    protected dialogRef: MatDialogRef<DprEditForecastActivityDialogEditComponent>,
    private appFacade: AppFacade,
    private userDetailsFacade: UserDetailsFacade,
    @Inject(MAT_DIALOG_DATA)
    public data: DialogData<DprForecastActivityGridData>,
    private dprActivityCustomDataService: DprActivityCustomDataService
  ) {
    super(dialogRef);
    this.context = this.data.context;

    this.initData();
  }

  private get syncDprForecastSubActivities(): GridSync<DprForecastSubActivityGridData> {
    const changes = this.subActivitiesGridApi.getChanges();

    return changes;
  }

  private get syncDprForecastActivityCustomDataList(): GridSync<DprForecastActivityCustomDataGridData> {
    const changes = this.customDataGridApi.getChanges();

    changes.addedList = changes.updatedList.filter((x) => isNaN(+x.id));
    changes.updatedList = changes.updatedList.filter((x) => !isNaN(+x.id));

    return changes;
  }

  private get isDirty(): boolean {
    return (
      this.subActivitiesGrid?.api.isDirty() ||
      this.customDataGrid?.api.isDirty()
    );
  }

  onDone(): void {
    if (!this.isDirty) {
      this.onCancel();
      return;
    }

    const syncDprForecastSubActivities = this.syncDprForecastSubActivities;
    const syncDprForecastActivityCustomDataList =
      this.syncDprForecastActivityCustomDataList;

    if (!this.subActivitiesGrid.validateChanges()) {
      return;
    }

    this.dialogRef.close({
      syncDprForecastSubActivities,
      syncDprForecastActivityCustomDataList,
      dprForecastSubActivities:
        this.context.data.dprForecastSubActivities.filter(
          (x) => !this.subActivitiesGrid.isRowEmpty(x)
        ),
      dprForecastActivityCustomDataList: this.dprActivityCustomDataGridData,
    });
  }

  onCancel(): void {
    if (!this.isDirty) {
      this.dialogRef.close();
      return;
    }

    canDeactivateUnsavedChanges(this, this._dialog)
      .pipe(
        untilDestroyed(this),
        filter((val) => val),
        tap(() => {
          this.dialogRef.close();
        })
      )
      .subscribe();
  }

  canDeactivate(): boolean | Observable<boolean> {
    return !this.isDirty;
  }

  save(): Observable<boolean> {
    this.onDone();
    return of(false);
  }

  onSubActivitiesGridRegistered(
    gridApi: GridApi<DprForecastSubActivityGridData>
  ): void {
    this.subActivitiesGridApi = gridApi;

    const data = this.context.data.syncDprForecastSubActivities;

    if (data) {
      this.subActivitiesGridApi.setPredefinedChanges({
        addedList: data.addedList,
        updatedList: data.updatedList,
        deletedIds: data.deletedIds,
        changedProperties: data.changedProperties,
      });
    }
  }

  onCustomDataGridRegistered(
    gridApi: GridApi<DprForecastActivityCustomDataGridData>
  ): void {
    this.customDataGridApi = gridApi;

    const data = this.context.data.syncDprForecastActivityCustomDataList;

    if (data) {
      this.customDataGridApi.setPredefinedChanges({
        addedList: [],
        updatedList: [...data.addedList, ...data.updatedList],
        deletedIds: data.deletedIds,
        changedProperties: data.changedProperties,
      });
    }
  }

  //#region Private methods

  private initData(): void {
    this.context.data = cloneDeep(this.context.data);

    this.activityMobilisation =
      this.context.additionalData.activityMobilisationList;

    this.isMilestoneActivity = this.context.additionalData?.isMilestoneActivity;
    this.selectedSubproject = this.context.additionalData.selectedSubproject;

    this.initSubprojectRelatedData();

    this.activeLink =
      this.context.additionalData?.selectedTab ?? DprForecastData.CustomData;
  }

  private initSubprojectRelatedData(): void {
    this.title = $localize`:@@dprDetails.dprEditForecastActivitiesDialog.titleEditDetails:${this.context.additionalData.title}:activityIdentifier:: Edit details`;

    this.customDataList = this.selectedSubproject.data.customDataList;

    this.customDataListValues =
      this.selectedSubproject.data.customDataValueList;

    const customDataGridData =
      this.dprActivityCustomDataService.buildForecastCustomDataGridData(
        this.selectedSubproject,
        this.context.data
      );

    this.dprActivityCustomDataGridData = customDataGridData.map((entry) => ({
      ...entry,
      dataTypeId: this.customDataList.find((cd) => cd.id === entry.customDataId)
        .dataTypeId,
    }));
  }

  //#endregion
}
